// ===========================================
// Create Enquiry Dialog Component
// ===========================================
// Dialog for staff to manually log an enquiry on behalf of a contact (enquiries plan §5.7, §7).
// Required (§7): the contact's FIRST name, an email OR a phone, the marketing SOURCE and the
// message (the channel is always MANUAL, set by the server). Several children, or none. The
// newsletter opt-in starts unticked. "Date received" defaults to the SCHOOL's today and cannot be
// a future day. Possible duplicates (the same email or phone on another enquiry, or a family that
// already has the email) are WARNED about, never refused. The new enquiry starts at New, owned by
// the school's New owner, who is emailed.

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateManualEnquiry, useEnquiryConfig, useEnquiryMatches } from '../../hooks/useEnquiries';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { MANUAL_ENQUIRY_LIMITS, type EnquiryTemperature, type ManualEnquiryData } from '../../api/enquiry.api';
import EnquiryOptionSelect from './enquiries/EnquiryOptionSelect';
import ChildrenEditor from './enquiries/ChildrenEditor';
import { ageError, childrenPayload, type ChildRow } from './enquiries/childRows';
import DuplicateWarning from './enquiries/DuplicateWarning';
import { TEMPERATURE_LABELS, stageView } from './enquiries/enquiryUi';

const TRIAL_TYPE_OPTIONS = [
  'Meet & Greet',
  'Phone Call',
  'Trial Lesson',
  'School Tour',
  'Other',
];

/** Mirrors the server's limit on the message. */
export const ENQUIRY_MESSAGE_MAX = MANUAL_ENQUIRY_LIMITS.message;
const L = MANUAL_ENQUIRY_LIMITS;
const AVAILABILITY_MAX = 500;

const hasDigit = (v: string | undefined) => !!v && /\d/.test(v);

// ===========================================
// VALIDATION SCHEMA
// ===========================================

const createEnquirySchema = z
  .object({
    firstName: z.string().trim().min(1, 'First name is required').max(L.name, 'First name too long'),
    lastName: z.string().trim().max(L.name, 'Last name too long'),
    email: z.string().trim().email('Invalid email address').or(z.literal('')),
    phone: z.string().max(L.phone, 'Phone number too long'),
    trialType: z.string().max(100),
    subject: z.string().max(L.subject, 'Subject too long'),
    message: z
      .string()
      .trim()
      .min(1, 'Message is required')
      .max(ENQUIRY_MESSAGE_MAX, `Message must be ${ENQUIRY_MESSAGE_MAX.toLocaleString('en-AU')} characters or fewer`),
    availability: z.string().max(AVAILABILITY_MAX, 'Availability too long'),
    temperature: z.enum(['', 'COLD', 'WARM', 'HOT']),
    newsletterOptIn: z.boolean(),
    enquirySourceId: z.string().min(1, 'Choose where this enquiry came from'),
    /** `YYYY-MM-DD`; '' = not touched, which means the school's today. */
    receivedOn: z.string(),
  })
  .refine((v) => v.email.trim() !== '' || hasDigit(v.phone), {
    message: 'Give an email address or a phone number',
    path: ['email'],
  });

type FormValues = z.infer<typeof createEnquirySchema>;

const EMPTY_VALUES: FormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  trialType: '',
  subject: '',
  message: '',
  availability: '',
  temperature: '',
  newsletterOptIn: false,
  enquirySourceId: '',
  receivedOn: '',
};

// ===========================================
// PROPS
// ===========================================

interface CreateEnquiryDialogProps {
  open: boolean;
  onClose: () => void;
}

// ===========================================
// COMPONENT
// ===========================================

export default function CreateEnquiryDialog({ open, onClose }: CreateEnquiryDialogProps) {
  const createMutation = useCreateManualEnquiry();
  const { data: config } = useEnquiryConfig();
  const schoolToday = config?.schoolToday;
  const newOwnerName = stageView(config, 'NEW')?.owner?.name;
  const temperatureEnabled = config?.settings.temperatureEnabled === true;
  const [children, setChildren] = useState<ChildRow[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(createEnquirySchema),
    defaultValues: EMPTY_VALUES,
  });

  const messageLength = watch('message').length;
  const busy = isSubmitting || createMutation.isPending;
  // Create stays unavailable until every required field holds something (Andrew, 23/10/2026: the
  // button looked usable with no source). The schema still judges the values on submit.
  const [firstName, email, phone, message, sourceId] = watch(['firstName', 'email', 'phone', 'message', 'enquirySourceId']);
  const childErrors = children.some((c) => ageError(c.age) !== null);
  const missing = [
    !firstName?.trim() && 'first name',
    !email?.trim() && !hasDigit(phone) && 'email or phone',
    !sourceId && 'source',
    !message?.trim() && 'message',
  ].filter((m): m is string => !!m);

  // The duplicate warning: asked only once the typing settles.
  const probeEmail = useDebouncedValue(email, 500);
  const probePhone = useDebouncedValue(phone, 500);
  const { data: matches } = useEnquiryMatches({ email: probeEmail, phone: probePhone }, open);

  const handleClose = () => {
    reset(EMPTY_VALUES);
    setChildren([]);
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    const receivedOn = values.receivedOn || schoolToday;
    // A future day is the server's 400; refuse it here with the field in view.
    if (receivedOn && schoolToday && receivedOn > schoolToday) return;
    if (childErrors) return;

    const data: ManualEnquiryData = {
      firstName: values.firstName,
      message: values.message,
      enquirySourceId: values.enquirySourceId,
      // New rows carry no id.
      children: childrenPayload(children),
      newsletterOptIn: values.newsletterOptIn,
      ...(values.lastName.trim() ? { lastName: values.lastName } : {}),
      ...(values.email.trim() ? { email: values.email.trim() } : {}),
      ...(values.phone.trim() ? { phone: values.phone.trim() } : {}),
      ...(receivedOn ? { receivedOn } : {}),
      ...(values.trialType ? { trialType: values.trialType } : {}),
      ...(values.subject ? { subject: values.subject } : {}),
      ...(values.availability.trim() ? { availability: values.availability.trim() } : {}),
      ...(temperatureEnabled && values.temperature ? { temperature: values.temperature as EnquiryTemperature } : {}),
    };

    try {
      await createMutation.mutateAsync(data);
    } catch {
      // The hook shows the server's reason; keep the dialog open with what was typed.
      return;
    }
    handleClose();
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Add Enquiry
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manually log an enquiry on behalf of a contact
        </Typography>
        {newOwnerName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            It will be owned by {newOwnerName}, who is emailed if that is someone other than you.
          </Typography>
        )}
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    inputProps={{ maxLength: L.name }}
                    required
                    fullWidth
                    size="small"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    inputProps={{ maxLength: L.name }}
                    fullWidth
                    size="small"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    type="email"
                    fullWidth
                    size="small"
                    error={!!errors.email}
                    helperText={errors.email?.message ?? 'An email or a phone number is enough'}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone"
                    type="tel"
                    inputProps={{ maxLength: L.phone }}
                    fullWidth
                    size="small"
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                )}
              />
            </Grid>

            {matches && (
              <Grid item xs={12}>
                <DuplicateWarning matches={matches} config={config} />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Controller
                name="enquirySourceId"
                control={control}
                render={({ field }) => (
                  <EnquiryOptionSelect
                    kind="SOURCE"
                    label="Source"
                    value={field.value}
                    onChange={field.onChange}
                    required
                    error={!!errors.enquirySourceId}
                    helperText={errors.enquirySourceId?.message ?? 'Where did this enquiry come from?'}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="receivedOn"
                control={control}
                render={({ field }) => {
                  const shown = field.value || schoolToday || '';
                  const future = !!schoolToday && shown > schoolToday;
                  return (
                    <TextField
                      {...field}
                      value={shown}
                      label="Date received"
                      type="date"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: schoolToday }}
                      error={future}
                      helperText={future ? 'The date received cannot be in the future' : undefined}
                    />
                  );
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <ChildrenEditor rows={children} onChange={setChildren} disabled={busy} />
            </Grid>

            <Grid item xs={12} sm={temperatureEnabled ? 6 : 12}>
              <Controller
                name="trialType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel id="create-enquiry-trial-label">Trial Type</InputLabel>
                    <Select {...field} labelId="create-enquiry-trial-label" label="Trial Type">
                      <MenuItem value="">
                        <em>Not specified</em>
                      </MenuItem>
                      {TRIAL_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {temperatureEnabled && (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="temperature"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="create-enquiry-temperature-label">Temperature</InputLabel>
                      <Select {...field} labelId="create-enquiry-temperature-label" label="Temperature">
                        <MenuItem value="">
                          <em>Not set</em>
                        </MenuItem>
                        {(Object.keys(TEMPERATURE_LABELS) as EnquiryTemperature[]).map((t) => (
                          <MenuItem key={t} value={t}>
                            {TEMPERATURE_LABELS[t]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Controller
                name="availability"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Availability"
                    placeholder="Optional, e.g. weekday afternoons after 3:30"
                    inputProps={{ maxLength: AVAILABILITY_MAX }}
                    fullWidth
                    size="small"
                    error={!!errors.availability}
                    helperText={errors.availability?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="subject"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Subject"
                    fullWidth
                    size="small"
                    error={!!errors.subject}
                    helperText={errors.subject?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="message"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Message"
                    required
                    fullWidth
                    multiline
                    minRows={4}
                    size="small"
                    error={!!errors.message}
                    helperText={
                      <>
                        {errors.message?.message ? `${errors.message.message}. ` : ''}
                        <span data-testid="message-counter">
                          {messageLength.toLocaleString('en-AU')} / {ENQUIRY_MESSAGE_MAX.toLocaleString('en-AU')}
                        </span>
                      </>
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="newsletterOptIn"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="The family asked to receive the newsletter"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        {/* What is still needed, on its own line above the buttons, so neither competes for the
            row (Andrew, 23/10/2026: a greyed Create took two lines beside the caption). */}
        {missing.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ px: 3, pt: 1.5, textAlign: 'right' }}
            data-testid="create-enquiry-missing"
          >
            Still needed: {missing.join(', ')}
          </Typography>
        )}
        <DialogActions>
          <Button onClick={handleClose} disabled={busy} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            disabled={busy || missing.length > 0 || childErrors}
            startIcon={busy ? <CircularProgress size={16} /> : undefined}
          >
            {busy ? 'Creating...' : 'Create Enquiry'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
